import React, { useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { APP_DISPLAY_NAME } from '../constants/branding';

const Register = () => {
  const { register, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    organization_name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [devVerifyUrl, setDevVerifyUrl] = useState(null);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.email || !formData.password) {
      setError('Email and password are required.');
      return;
    }
    setLoading(true);
    const result = await register(formData);
    setLoading(false);
    if (!result.success) {
      setError(result.message || 'Registration failed.');
      return;
    }
    setDevVerifyUrl(result.verifyUrl || null);
    setDone(true);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: 'calc(100vh - 64px)'
        }}
      >
        <Card elevation={3} sx={{ width: '100%' }}>
          <CardContent sx={{ py: 4, px: 3 }}>
            <Typography
              component="h1"
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 700,
                letterSpacing: '0.28em',
                color: 'primary.dark',
                fontSize: { xs: '1.5rem', sm: '1.75rem' }
              }}
            >
              {APP_DISPLAY_NAME}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Create your account
            </Typography>

            {done && (
              <Alert severity={devVerifyUrl ? 'info' : 'success'} sx={{ mb: 2 }}>
                {devVerifyUrl ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      This server did not send an email (for example, Amazon SES is not configured). Open the link
                      below to verify your account, then you can sign in.
                    </Typography>
                    <Link href={devVerifyUrl} target="_blank" rel="noopener noreferrer">
                      {devVerifyUrl}
                    </Link>
                  </>
                ) : (
                  'Check your email for a verification link. After you verify, you can sign in. If you do not see it within a few minutes, check your spam folder.'
                )}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!done ? (
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password (at least 8 characters)"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="first_name"
                  label="First name"
                  id="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  name="last_name"
                  label="Last name"
                  id="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  name="organization_name"
                  label="Organization name (optional)"
                  id="organization_name"
                  value={formData.organization_name}
                  onChange={handleChange}
                  disabled={loading}
                  helperText="Defaults to “First name’s organization” if left blank."
                />
                <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading}>
                  {loading ? <CircularProgress size={24} /> : 'Sign up'}
                </Button>
              </Box>
            ) : null}

            <Typography variant="body2" sx={{ mt: 2 }}>
              <Link component={RouterLink} to="/login">
                Back to sign in
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Register;
